import React, { useEffect, useState, useContext } from 'react';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { Pencil, Camera, Mail, Lock, User, Plus, X, Trash2, Database, Eye, EyeOff } from 'lucide-react';
import StudentAvatar from '../components/StudentAvatar';

const UserManagement = () => {
  const { user } = useContext(AuthContext);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({ username: '', full_name: '', email: '', password: '', photo: null });
  const [backingUp, setBackingUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fetchStaff = async () => {
    try {
      const res = await api.get('/users');
      setStaff(res.data);
    } catch (err) {
      setError('Failed to fetch users. Ensure you have Admin rights.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'Admin') {
      setError('You do not have permission to view this page.');
      setLoading(false);
      return;
    }
    fetchStaff();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    const data = new FormData();
    data.append('username', formData.username);
    data.append('full_name', formData.full_name);
    data.append('email', formData.email);
    if (formData.password) data.append('password', formData.password);
    if (formData.photo) data.append('photo', formData.photo);
    if (editingStaff) data.append('photo_url', editingStaff.photo_url || '');

    try {
      if (editingStaff) {
        await api.put(`/users/${editingStaff.id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSuccess('Staff profile updated successfully.');
      } else {
        await api.post('/users', { 
          username: formData.username, 
          password: formData.password,
          full_name: formData.full_name,
          email: formData.email
        });
        setSuccess('Staff account created successfully.');
      }
      setFormData({ username: '', full_name: '', email: '', password: '', photo: null });
      setEditingStaff(null);
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process request.');
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      username: staffMember.username,
      full_name: staffMember.full_name || '',
      email: staffMember.email || '',
      password: '',
      photo: null
    });
  };
 
  const handleDelete = async (id, name) => {
      if (!window.confirm(`Are you sure you want to delete ${name}? They will lose all access to the system.`)) return;
      try {
          await api.delete(`/users/${id}`);
          setSuccess('User removed from system.');
          fetchStaff();
      } catch (err) {
          setError(err.response?.data?.error || 'Failed to delete user.');
      }
  };

  const cancelEdit = () => {
    setEditingStaff(null);
    setFormData({ username: '', full_name: '', email: '', password: '', photo: null });
  };

  const handleBackupGenerate = async () => {
    setBackingUp(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post('/dashboard/backup/generate');
      setSuccess(res.data.message || 'Database backup successfully created on Google Drive!');
    } catch (err) {
      setError(err.response?.data?.error || 'Database backup failed.');
    } finally {
      setBackingUp(false);
    }
  };

  if (loading) return <div>Loading User Management...</div>;
  
  if (user?.role !== 'Admin') {
      return <div className="text-red-500 font-bold p-8 card h-64 flex items-center justify-center">Access Denied. Admins Only.</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Manage Staff Accounts</h1>

      {success && <div className="mb-4 p-3 bg-green-100 text-green-700 text-sm rounded">{success}</div>}
      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Col: Form and Backup */}
        <div className="space-y-6 lg:col-span-1">
          <div className={`card h-fit border-t-4 ${editingStaff ? 'border-amber-500' : 'border-indigo-500'}`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800">
              {editingStaff ? <Pencil size={20} className="text-amber-600" /> : <Plus size={20} className="text-indigo-600" />}
              {editingStaff ? 'Edit Staff Details' : 'Add New Staff'}
            </h2>
            {editingStaff && (
              <button onClick={cancelEdit} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400">
                <X size={18} />
              </button>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {editingStaff && (
              <div className="flex justify-center mb-6">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-2xl bg-gray-100 overflow-hidden border-2 border-gray-100 flex items-center justify-center">
                    {formData.photo ? (
                      <img src={URL.createObjectURL(formData.photo)} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <StudentAvatar photoUrl={editingStaff.photo_url} fullName={editingStaff.full_name || editingStaff.username} className="w-full h-full" />
                    )}
                  </div>
                  <label className="absolute bottom-[-8px] right-[-8px] bg-white p-2 rounded-xl shadow-lg border border-gray-100 cursor-pointer hover:bg-gray-50 transition-all">
                    <Camera size={16} className="text-indigo-600" />
                    <input type="file" className="hidden" onChange={e => setFormData({...formData, photo: e.target.files[0]})} />
                  </label>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                <User size={14} />
                Username
              </label>
              <input 
                type="text" 
                className="input-field" 
                required 
                value={formData.username}
                onChange={e => setFormData({...formData, username: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                <User size={14} />
                Full Name
              </label>
              <input 
                type="text" 
                className="input-field" 
                placeholder="Staff Full Name"
                value={formData.full_name}
                onChange={e => setFormData({...formData, full_name: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                <Mail size={14} />
                Email Address
              </label>
              <input 
                type="email" 
                className="input-field" 
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                placeholder="staff@example.com"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                <Lock size={14} />
                {editingStaff ? 'Change Password' : 'Temporary Password'}
              </label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"} 
                  className="input-field pr-10" 
                  required={!editingStaff}
                  placeholder={editingStaff ? 'Leave empty to keep current' : '••••••••'}
                  value={formData.password}
                  onChange={e => setFormData({...formData, password: e.target.value})}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button type="submit" className={`btn-primary w-full mt-2 ${editingStaff ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600'}`}>
              {editingStaff ? 'Update Profile' : 'Create Account'}
            </button>
          </form>
        </div>

        {/* Database Backup Section */}
        <div className="card h-fit border-t-4 border-emerald-500 space-y-4">
           <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800">
             <Database className="text-emerald-600" size={20} />
             System Backups
           </h2>
           <p className="text-xs text-gray-500 leading-relaxed font-medium">
             Generate a manual snapshot of the database and upload it securely to Google Drive under the Emergency backups folder.
           </p>
           <button 
             onClick={handleBackupGenerate}
             disabled={backingUp}
             className="w-full py-2.5 rounded-xl font-bold text-sm text-white bg-emerald-600 hover:bg-emerald-700 active:scale-98 transition-all disabled:opacity-50"
           >
             {backingUp ? 'Generating Backup...' : 'Backup Database Now'}
           </button>
        </div>
      </div>

        {/* Right Col: Table */}
        <div className="card lg:col-span-2 overflow-x-auto p-0">
           <table className="w-full text-left">
            <thead className="bg-gray-50 border-b text-gray-500 text-sm">
                <tr>
                    <th className="p-4">Staff Member</th>
                    <th className="p-4">Role</th>
                    <th className="p-4">Start Date</th>
                    <th className="p-4 text-right">Actions</th>
                </tr>
            </thead>
            <tbody>
                {staff.length === 0 && (
                    <tr><td colSpan="3" className="p-4 text-center text-gray-400">No staff accounts found.</td></tr>
                )}
                {staff.map(s => (
                    <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/80 transition-colors group">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <StudentAvatar photoUrl={s.photo_url} fullName={s.full_name || s.username} className="w-10 h-10 rounded-xl" />
                            <div>
                              <p className="font-bold text-gray-900 leading-none mb-1">{s.full_name || s.username}</p>
                              {s.email ? (
                                <a href={`mailto:${s.email}`} className="text-xs text-indigo-600 hover:underline font-medium">{s.email}</a>
                              ) : (
                                <p className="text-xs text-gray-400 font-medium">No email set</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-[10px] font-black uppercase tracking-tighter border border-indigo-100">
                            {s.role}
                          </span>
                        </td>
                        <td className="p-4 text-xs text-gray-500 font-medium">{new Date(s.created_at).toLocaleDateString()}</td>
                        <td className="p-4 text-right">
                          <div className="flex justify-end items-center gap-2">
                            <button 
                              onClick={() => handleEdit(s)}
                              className="p-2 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                              title="Edit Staff Profile"
                            >
                              <Pencil size={18} />
                            </button>
                            <button 
                              onClick={() => handleDelete(s.id, s.full_name || s.username)}
                              className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                              title="Revoke Access"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                    </tr>
                ))}
            </tbody>
           </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
